import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, ArrowLeft, CheckCircle2 } from 'lucide-react';
import logo from '../assets/images/pulso-logo.png';
import '../styles/pulso.css';

function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSent, setIsSent] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    if (!email) {
      setError('Please enter your email');
      setIsLoading(false);
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || 'Something went wrong. Please try again.');
        setIsLoading(false);
        return;
      }

      setIsSent(true);
    } catch (err) {
      setError('Network error. Please check your connection.');
    }
    setIsLoading(false);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-md animate-fade-in">
        {/* Logo and Title */}
        <div className="mb-8 flex flex-col items-center text-center">
          <div className="mb-4">
            <img src={logo} alt="Pulso" style={{ width: 56, height: 'auto' }} />
          </div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: '#38394e' }}>PULSO</h1>
          <p className="mt-1 text-muted-foreground">by Imaginarium</p>
        </div>

        {/* Card */}
        <div className="rounded-lg border border-border bg-card text-card-foreground shadow-sm">
          {/* Card Header */}
          <div className="flex flex-col space-y-1.5 p-6">
            <h3 className="text-xl font-semibold leading-none tracking-tight">
              {isSent ? 'Check your email' : 'Forgot password'}
            </h3>
            <p className="text-sm text-muted-foreground">
              {isSent
                ? 'We\'ve sent you a link to reset your password.'
                : 'Enter your email and we\'ll send you a reset link.'}
            </p>
          </div>

          {/* Card Content */}
          <div className="p-6 pt-0">
            {isSent ? (
              <div className="space-y-4">
                <div className="rounded-lg bg-green-50 border border-green-200 p-4 flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-green-800">
                    <p className="font-medium mb-1">Reset link sent</p>
                    <p>If an account with that email exists, you'll receive an email with instructions. The link expires in 1 hour.</p>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Didn't get an email? Check your spam folder, or{' '}
                  <button
                    onClick={() => { setIsSent(false); setEmail(''); }}
                    className="font-medium hover:underline"
                    style={{ color: '#395b81' }}
                  >
                    try again
                  </button>
                  .
                </p>
                <Link
                  to="/login"
                  className="inline-flex items-center text-sm font-medium hover:underline"
                  style={{ color: '#395b81' }}
                >
                  <ArrowLeft className="mr-1 h-4 w-4" />
                  Back to login
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-800">
                    {error}
                  </div>
                )}

                <div className="space-y-2">
                  <label
                    htmlFor="email"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={isLoading}
                    autoFocus
                    className="flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-11 w-full"
                  style={{ background: '#f0856d', color: 'white' }}
                  onMouseEnter={e => { if (!isLoading) e.currentTarget.style.opacity = '0.88'; }}
                  onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send reset link'
                  )}
                </button>

                <div className="pt-2 text-center">
                  <Link
                    to="/login"
                    className="inline-flex items-center text-sm font-medium hover:underline"
                    style={{ color: '#395b81' }}
                  >
                    <ArrowLeft className="mr-1 h-4 w-4" />
                    Back to login
                  </Link>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ForgotPassword;